import type { Signal, Suggestion, AdapterConfig, OperatorConfig, EventPack, LiveMatch } from "./types";

export interface PageContext {
  live_matches: Array<Record<string, unknown>>;
  odds_snapshot: Array<Record<string, unknown>>;
  betslip: Array<Record<string, unknown>>;
  /** ⭐ The OPEN pre-match fixture's ladder — a fixture the visitor has open
   *  that is not on the live board, so its lines could never ride on a
   *  `live_matches` entry. Absent unless it is both resolved and primed. */
  open_event_markets?: { event_id: string; lines: unknown[] };
  /** ⭐ The OPEN fixture as a board entry, when no board this client holds
   *  describes it (beyond the pre-match horizon, or a session that never pulled
   *  the pre-match board). Identity from the app's store, 1X2 from the ladder. */
  open_event?: Record<string, unknown>;
}

export interface ConfigResponse {
  config: OperatorConfig;
  adapter: AdapterConfig;
  event_packs: EventPack[];
}

// Injected at bundle time (esbuild `define`, from build-version.mjs) — a content
// hash of src/, so it changes exactly when the code changes and never otherwise.
//
// ⛔ Was the literal "1" from the day the SDK shipped, and the backend never read
// it. That made propagation unmeasurable: with an SPA, a session that never
// reloads keeps running an old bundle for hours, and the only evidence of who was
// on what was a side effect (a new field appearing in the payload) that expires
// the moment every client has turned over. The backend now records this per event
// in companion_events.sdk_version.
//
// "dev" when nothing defined it — a source checkout run outside the bundler.
declare const __SDK_BUILD__: string;
const SDK_VERSION = typeof __SDK_BUILD__ === "string" ? __SDK_BUILD__ : "dev";
// Capabilities this build has, sent on every /suggestions call. Add a name here
// in the SAME commit as the renderer branch that can draw it — the whole point
// is that the claim and the ability ship together.
const SDK_FEATURES = ["prematch", "tiers", "fact_card", "fact_card_diagnostics", "match_brief_v1", "match_commentary_0731"];
const SESSION_COOKIE = "bc_session_id";
const SESSION_MAX_AGE = 30 * 60; // 30 minutes in seconds
// Durable first-party VISITOR id (distinct from the 30-min session): a stable
// per-browser id we control, used to accumulate behaviour across sessions for
// personalization. First-party, single-site — no cross-site tracking. Digitain's
// own user id is NOT reachable (iframe-internal), so this is the right identity.
const VISITOR_COOKIE = "bc_visitor_id";
const VISITOR_MAX_AGE = 365 * 24 * 60 * 60; // 1 year
const SUGGESTIONS_TIMEOUT_MS = 3000;

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class CompanionApiClient {
  private apiBase: string;
  private apiKey: string;
  private briefEventQueue: Promise<unknown> = Promise.resolve();
  /** Seconds the SERVER asked us to wait before polling again.
   *
   * ⛔ This used to be parsed and thrown away. `/suggestions` has always
   * returned `ttl` (30 s), and the SDK polled on a hardcoded 5 s interval
   * anyway — up to 12x the server's own contract once the 2.5 s event-driven
   * floor is counted. On 2026-08-26 that put the live pilot into queue
   * collapse: ~717 req/min against 4 saturated workers, accept queue 1,622
   * deep, every client dropped. Per-request cost had already been cut 7x that
   * day and it made no difference, because the binding constraint was request
   * VOLUME, not the cost of one.
   *
   * Null until the first successful response, so the caller can fall back to
   * its own default rather than guessing zero. */
  private ttlS: number | null = null;

  /** The server told THIS build to stop (`disabled: true` on /suggestions).
   *
   * ⭐ THE RETIREMENT SWITCH, and it deliberately rides /suggestions rather than
   * /config. `fetchConfig()` runs once at boot, so a retire decision carried
   * there would only land on a page load — which is the one moment the browser
   * was going to pick up the new bundle anyway, making the lever useless. This
   * endpoint is polled, and it already receives `X-SDK-Version`, so the server
   * can name a single bad build and reach every running session of it in one
   * poll — including inside an SPA that never reloads.
   *
   * Why this exists at all: on 2026-08-27 the bundle was served
   * `Cache-Control: immutable, max-age=2592000` on an unversioned URL, which
   * pinned ~1/3 of visitors to one build for 30 days with no way to reach them.
   * The header is fixed (`max-age=300`, verified live 2026-09-02), but a bundle
   * that carries its own off switch does not depend on that staying fixed.
   *
   * One-way for the life of the page: a retired build does not un-retire on a
   * later poll, because it has already torn its DOM down and stopped asking. */
  private retired = false;

  /** The build the server says is current (`X-SDK-Current` on /suggestions). */
  private currentBuild: string | null = null;

  /** The `deck` object of the most recent DECK request's response, or `null`
   *  when that request carried none (or the most recent call wasn't a deck
   *  request at all — the field only ever means something in that context). */
  private deck: { page: number; has_more: boolean } | null = null;

  constructor(apiBase: string, apiKey: string) {
    this.apiBase = apiBase.replace(/\/$/, "");
    this.apiKey = apiKey;
  }

  /** How long to wait before the next poll, in ms.
   *
   * Clamped: a server that returns nonsense (0, negative, absent, absurd)
   * must never turn this into a hot loop against our own API, and must never
   * park a client for an hour either. */
  /** Has the server retired this build? The host tears the widget down and stops
   *  polling on the first true. */
  isRetired(): boolean {
    return this.retired;
  }

  /** The current build when THIS bundle is not it, else null. A dev build never
   *  swaps. See self-update.ts. */
  upgradeTarget(): string | null {
    if (SDK_VERSION === "dev" || !this.currentBuild || this.currentBuild === SDK_VERSION) return null;
    return this.currentBuild;
  }

  /** The `deck` object of the most recent deck request's response, or `null`. */
  lastDeck(): { page: number; has_more: boolean } | null {
    return this.deck;
  }

  /** Live event ids the last /suggestions response found fact-covered (served or
   *  not). The next poll posts their live-feed lines — see `coveredForLines`. */
  lastCoveredLive(): string[] {
    return this.coveredLive;
  }
  private coveredLive: string[] = [];

  pollIntervalMs(fallbackMs: number): number {
    if (this.ttlS == null || !Number.isFinite(this.ttlS)) return fallbackMs;
    const ms = this.ttlS * 1000;
    return Math.min(Math.max(ms, 5000), 120000);
  }

  async fetchConfig(): Promise<ConfigResponse | null> {
    try {
      const res = await fetch(`${this.apiBase}/api/companion/config/${this.apiKey}`);
      if (!res.ok) return null;
      return (await res.json()) as ConfigResponse;
    } catch {
      return null;
    }
  }

  async fetchHoldout(account: string): Promise<import("./holdout").HoldoutDecision|null> {
    const abort=new AbortController();
    const timer=setTimeout(()=>abort.abort(),SUGGESTIONS_TIMEOUT_MS);
    try {
      const response=await fetch(`${this.apiBase}/api/companion/experiment/assignment`,{
        method:"POST",headers:{"Content-Type":"application/json","X-API-Key":this.apiKey,"X-SDK-Version":SDK_VERSION},
        body:JSON.stringify({user_hash:account,capability:"holdout-v1"}),signal:abort.signal});
      if(!response.ok)return null;
      const value=await response.json();
      if(value.id!=="parikara-ab-v1" || typeof value.widget_enabled!=="boolean" ||
        !["running","inactive","excluded"].includes(value.status) ||
        (value.status==="running" && (!["widget","control"].includes(value.arm) || value.widget_enabled!==(value.arm==="widget") || typeof value.ends_at!=="string" || !Number.isFinite(Date.parse(value.ends_at.replace(" ","T")+"Z")))))return null;
      return value;
    } catch{return null;} finally{clearTimeout(timer);}
  }
  private holdoutReader: ((account:string)=>Record<string,string>)|null=null;
  private holdoutHidden: (()=>boolean)|null=null;
  setHoldoutReader(reader:(account:string)=>Record<string,string>,hidden:()=>boolean):void {
    this.holdoutReader=reader;this.holdoutHidden=hidden;
  }

  async fetchSuggestions(
    signals: Signal[],
    pageContext: PageContext,
    locale: string,
    device: string,
    extra?: {
      current_context?: Record<string, unknown> | null;
      browse_history?: Array<Record<string, unknown>>;
      dismissed_ids?: string[];
      // ⛔ SEPARATE FROM dismissed_ids, and the separation is the point: a skip
      // releases the open match, an add HOLDS it. See the two-list comment in
      // index.ts.
      added_ids?: string[];
      favourites?: Record<string, unknown> | null;
      bet_history?: Record<string, unknown> | null;
      route?: { path: string; search: string } | null;
      // The swipe deck's own paging. Sent ONLY when `deck_page` is a number —
      // the API's legacy discriminator is KEY PRESENCE of `deck_page` in the
      // request body, so an absent key (not a 0 or undefined value) is what
      // keeps a non-deck caller on the old behaviour.
      deck_page?: number;
      seen_ids?: string[];
    },
  ): Promise<Suggestion[] | null> {
    const isDeckRequest = typeof extra?.deck_page === "number";
    const visitorId = this.getVisitorId();
    const stableIdentity = visitorId === this.getVisitorId();
    // Returns null on failure (timeout/429/network) — NOT a stale cache. Serving
    // the previous list as if fresh made the widget claim "updated now" on stuck
    // data (the frozen suspended-line card from the 2026-07-07 soak); the caller
    // keeps the current DOM and the staleness shows honestly in the subtitle.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SUGGESTIONS_TIMEOUT_MS);

    try {
      const res = await fetch(`${this.apiBase}/api/companion/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
          "X-SDK-Version": SDK_VERSION,
          // This build can hand its request shadow to a swapped-in bundle
          // (adapter.ts ShadowHost); only such builds are told to self-update.
          "X-SDK-Caps": "handoff",
        },
        body: JSON.stringify({
          session_id: this.getSessionId(),
          // What THIS bundle can render. ⛔ A capability list, not a version:
          // the server cannot tell from a build hash whether a bundle knows how
          // to draw a card, and a hand-kept list of "new enough" hashes is the
          // kind of thing that goes stale silently.
          //
          // It exists because the SDK arrives SLOWLY: the bundle is served
          // `immutable` for 30 days and a session never re-fetches it (measured
          // 2026-08-27: a fresh build reached ~16% of visitors after 96 minutes).
          // The pre-match board is cached per OPERATOR, so one new-bundle visitor
          // fills a board that every old-bundle visitor would then be ranked
          // against — and an old renderer has no pre-match branch, so it would
          // draw a fixture that has not started as bare team names with no
          // countdown. Sending the list means only bundles that can draw the card
          // are offered one; everyone else is served exactly as they are today.
          //
          // ⛔ In the BODY, not a header. `allow_headers` in app.py is a closed
          // CORS list, and a header missing from it fails the preflight — which
          // would take down every /suggestions POST from the browser, not just
          // this feature.
          features: stableIdentity && typeof IntersectionObserver !== "undefined" ? SDK_FEATURES : SDK_FEATURES.filter(f => f !== "match_brief_v1" && f !== "match_commentary_0731"),
          // Durable visitor id → the backend personalizes ranking from this
          // browser's accumulated add/skip/place history across sessions.
          visitor_id: visitorId,
          signals,
          page_context: pageContext,
          locale,
          device,
          // Digitain targeting: the event being browsed + in-session history.
          current_context: extra?.current_context ?? null,
          browse_history: extra?.browse_history ?? [],
          // Skipped suggestion ids — the backend excludes these and fills the
          // slots with next-best picks (skip = "show me something else").
          dismissed_ids: extra?.dismissed_ids ?? [],
          added_ids: extra?.added_ids ?? [],
          // The user's own ★ Фавориты, read passively off the operator's client
          // (favourites.ts). Absent for every operator but Digitain, and absent
          // for a Digitain user who has never opened that tab — the backend then
          // ranks exactly as it does today. Carries no identity: starred team and
          // league names only, never the operator's user id.
          favourites: extra?.favourites ?? null,
          // A DERIVED taste profile from the user's own bet history — counts and
          // win rates per sport / league / odds band, never the bets themselves.
          // Absent until the user has opened their history at least once.
          bet_history: extra?.bet_history ?? null,
          route: extra?.route ?? null,
          ...(isDeckRequest
            ? { deck_page: extra!.deck_page, seen_ids: extra?.seen_ids ?? [] }
            : {}),
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        return null;
      }

      const data = (await res.json()) as {
        suggestions: Suggestion[];
        ttl?: number;
        retired?: boolean;
        deck?: { page: number; has_more: boolean };
        covered_live?: unknown;
      };
      // ⛔ `retired`, NOT `disabled`. They are different powers and conflating
      // them would cost us the one that matters more. `disabled` is the operator
      // emergency brake (`widget_enabled: off`): it means "show nothing right
      // now" and its whole value is that flipping it back reaches a running
      // session on the next poll. `retired` means "this BUILD is bad, stop
      // executing" and is terminal by design. A brake that tore the DOM down
      // would need a page reload to undo, which is exactly the property the
      // brake was built not to have.
      //
      // ⛔ Latched, never cleared. See `retired`.
      if (data.retired === true) this.retired = true;
      const current = res.headers?.get?.("X-SDK-Current");
      if (current) this.currentBuild = current;
      // Record what the server asked for. Kept OFF the return value on purpose:
      // every call site wants suggestions, and threading a tuple through them
      // to carry one number is how the ttl got ignored in the first place.
      if (typeof data.ttl === "number" && data.ttl > 0) {
        this.ttlS = data.ttl;
      }
      // Only meaningful in the context of a deck request — see the field
      // comment above. A non-deck response's `deck` (if present at all) is
      // never something a caller asked for.
      this.deck = isDeckRequest ? (data.deck ?? null) : null;
      // Attacker-shaped defence is not needed (our own server), but a malformed
      // or absent field must read as "none", never throw inside the poll.
      this.coveredLive = Array.isArray(data.covered_live)
        ? (data.covered_live as unknown[]).slice(0, 12).map((x) => String(x))
        : [];
      return data.suggestions ?? [];
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** The language the widget is currently rendering in.
   *
   * ⛔ Stamped on EVERY event. The owner asked whether acceptance varies by
   * language; `locale` was on the advice row but on no impression or add, so
   * the only way to ask was to infer a session's language from other events —
   * which gave 1,049 Turkmen sessions just 540 impressions and an implausible
   * 52% add rate against an overall ~5%. Inference is not good enough here.
   */
  private locale = "";

  setLocale(locale: string): void {
    this.locale = String(locale || "");
  }

  /** Reads the operator's login state (boolean only, never an account id).
   *  Stamped on every event as `logged_in`, so a visitor's anonymous adds can be
   *  joined to their later login and bets. null = unreadable, not stamped. */
  private authReader: (() => boolean | null) | null = null;

  setAuthReader(fn: () => boolean | null): void {
    this.authReader = fn;
  }

  /** Pseudonymous account key (user-hash.ts) — never the raw id. null = none. */
  private userHashReader: (() => string | null) | null = null;

  setUserHashReader(fn: () => string | null): void {
    this.userHashReader = fn;
  }

  private experimentReader: ((account: string) => string | null) | null = null;

  setExperimentReader(fn: (account: string) => string | null): void {
    this.experimentReader = fn;
  }

  private timeStateReader: ((account: string) => 'foreground' | 'background' | 'unmounted' | null) | null = null;

  setTimeStateReader(fn: (account: string) => 'foreground' | 'background' | 'unmounted' | null): void {
    this.timeStateReader = fn;
  }

  /** Acked diagnostic-only evidence. It never creates a funnel impression. */
  async sendFactDiagnosticExposure(
    suggestionId: string, eventId: string,
    reference: { id: string; on_current_match: boolean | null },
  ): Promise<boolean | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), SUGGESTIONS_TIMEOUT_MS);
    try {
      let userHash: string | null = null;
      try { userHash = this.userHashReader?.() ?? null; } catch { /* same optional account as events */ }
      const response = await fetch(`${this.apiBase}/api/companion/fact-diagnostics/exposure`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": this.apiKey, "X-SDK-Version": SDK_VERSION },
        body: JSON.stringify({ session_id: this.getSessionId(), visitor_id: this.getVisitorId(),
          suggestion_id: suggestionId, event_id: eventId, fact_diagnostics: reference, user_hash: userHash }),
        signal: controller.signal,
      });
      if (response.status === 400 || response.status === 404 || response.status === 409) return null;
      if (!response.ok) return false;
      return (await response.json() as { status?: string }).status === "matched";
    } catch { return false; }
    finally { clearTimeout(timeout); }
  }

  sendEvent(
    sessionId: string,
    event: string,
    suggestionId: string,
    bookNumber?: string | null,
    meta?: { event_id?: string; rec_type?: string } & Record<string, unknown>,
  ): Promise<void> {
    const body: Record<string, unknown> = {
      // ⛔ Was `session_id: sessionId`, and every one of the 25 call sites (24 in
      // index.ts, 1 in impression.ts) passes "" — so 100% of companion_events
      // landed with no session and the dashboard put every visitor in a single
      // "session". The asymmetry was the tell: visitor_id below has always come
      // from the cookie. The cookie is the source of truth for both; the
      // argument is an override for a caller that has a better answer.
      session_id: sessionId || this.getSessionId(),
      // Durable visitor id on EVERY event (call sites pass "" for session on the
      // Digitain path; the visitor id is what per-user behaviour keys on).
      visitor_id: this.getVisitorId(),
      event,
      suggestion_id: suggestionId,
    };
    // Digitain attribution: the injected coupon booking number, if any.
    if (bookNumber) body.book_number = bookNumber;
    // ⛔ The locale rides on every event, with or without other meta. A caller
    // that passed one already wins — the same rule session_id uses.
    const withLocale = (this.locale && !(meta && meta.locale !== undefined))
      ? { ...(meta || {}), locale: this.locale }
      : meta;
    const stamped: Record<string, unknown> & { event_id?: string; rec_type?: string } =
      { ...(withLocale || {}) };
    if (this.authReader && stamped.logged_in === undefined) {
      let li: boolean | null = null;
      try { li = this.authReader(); } catch { li = null; }
      if (typeof li === "boolean") stamped.logged_in = li;
    }
    if (this.userHashReader && stamped.user_hash === undefined) {
      let uh: string | null = null;
      try { uh = this.userHashReader(); } catch { uh = null; }
      if (uh) stamped.user_hash = uh;
    }
    // Clock flushes carry their own old-account/interval observation. Never
    // overwrite those with the new account's or page's current visibility.
    if (event !== "sportsbook_time" && typeof stamped.user_hash === "string" && this.timeStateReader) {
      const state = this.timeStateReader(stamped.user_hash);
      if (state) { stamped.time_tracking_version = 1; stamped.time_state = state; }
    }
    // A suppressed renderer can still finish an in-flight paint callback. It
    // was not an exposure. Money, time, account and host activity always continue.
    if(this.holdoutHidden?.() && ["impression","pill_impression","peek_open","panel_open","pill_push"].includes(event))return Promise.resolve();
    delete stamped.ab_run; delete stamped.ab_state;
    if(typeof stamped.user_hash==="string" && this.holdoutReader)Object.assign(stamped,this.holdoutReader(stamped.user_hash));
    // Explicit account overrides (e.g. a flush after logout) keep their own assignment.
    delete stamped.controlled_experiment_id;
    delete stamped.controlled_experiment_arm;
    if (typeof stamped.user_hash === "string" && this.experimentReader) {
      const experiment = this.experimentReader(stamped.user_hash);
      if (experiment) stamped.controlled_experiment_id = experiment;
    }
    {
      // event_id/rec_type are first-class DB columns; everything else defined
      // (legs, combined_odds, booked, attributed, …) rides in the meta JSON.
      const { event_id, rec_type, ...rest } = stamped;
      if (event_id) body.event_id = event_id;
      if (rec_type) body.rec_type = rec_type;
      const extras: Record<string, unknown> = {};
      for (const k of Object.keys(rest)) {
        if (rest[k] !== undefined && rest[k] !== null) extras[k] = rest[k];
      }
      if (Object.keys(extras).length) body.meta = extras;
    }

    const post = () => fetch(`${this.apiBase}/api/companion/events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": this.apiKey,
        "X-SDK-Version": SDK_VERSION,
      },
      body: JSON.stringify(body),
      ...((event === "sportsbook_time" || event === "experiment_presence") ? { keepalive: true } : {}),
    }).catch(() => {});

    // Keep exposure and its outcomes in ingestion order without blocking the UI.
    if (event === "brief_interaction" || stamped.brief) {
      this.briefEventQueue = this.briefEventQueue.then(post, post);
    } else void post();
    return Promise.resolve();
  }

  /** POST the pre-match board. Fire-and-forget: a failure costs one 5-minute
   *  cycle and the server keeps serving the previous board until its TTL.
   *
   *  ⛔ A separate call on a separate clock, never a field on /suggestions. That
   *  payload is already ~110KB on a 5s timer, and capacity is the constraint the
   *  API box actually hits. */
  async postPrematchBoard(board: LiveMatch[], locale: string): Promise<boolean> {
    try {
      const res = await fetch(`${this.apiBase}/api/companion/prematch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.apiKey,
          "X-SDK-Version": SDK_VERSION,
        },
        // ⛔ The LANGUAGE OF THESE NAMES, and it is not optional. Unlike the
        // live board — which every visitor sends for themselves on every poll —
        // this one is posted by ONE visitor and read by ALL of them. Digitain
        // sends team names in the language of the session that fetched them and
        // carries no English pair for teams, so an unlabelled board would be
        // served to visitors reading another alphabet. The server files it under
        // this locale and never serves it to another.
        body: JSON.stringify({ board, locale }),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  getSessionId(): string {
    return this.getCookieId(SESSION_COOKIE, SESSION_MAX_AGE);
  }

  /** Stable per-browser visitor id (durable cookie) for cross-session personalization. */
  getVisitorId(): string {
    return this.getCookieId(VISITOR_COOKIE, VISITOR_MAX_AGE);
  }

  private getCookieId(name: string, maxAge: number): string {
    if (typeof document === "undefined") return generateId();
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (match) return match[1];
    const id = generateId();
    document.cookie = `${name}=${id}; max-age=${maxAge}; path=/; SameSite=Lax`;
    return id;
  }
}
